import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Grid,
  Paper,
  Box,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { marketDataService } from "../../utils/services/marketDataService";
import CustomTypography from "../../globalStyles/fonts";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useNavigate } from "react-router-dom";
import { isMobile } from "../../globalStyles/mobile";

const ManageMarketData = ({ open, handleClose, onAddData }) => {
  const [selectedDropskipTool, setSelectedDropskipTool] = useState(null);
  const [dropskipToolId, setDropskipToolId] = useState();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    avg_order_weight: "",
    avg_units_per_order: "",
    avg_order_value: "",
    cogs: "",
    unit_cogs: "",
    b2b_fulfillment_cost: "",
    ds_fulfillment_cost: "",
    merchandise: [
      {
        name: "",
        hs_code: "",
        duty_non_china: "",
        duty_rate_china: "",
        primary: false,
      },
    ],
  });
  const navigate = useNavigate();
  const fetchDropskipToolById = async (id) => {
    setLoading(true);
    try {
      const response = await marketDataService.fetchById(id);
      setSelectedDropskipTool(JSON.parse(JSON.stringify(response)));
      setLoading(false);
    } catch (error) {
      console.error("Fetch failed:", error);
      toast.error(error);
    }
  };
  useEffect(() => {
    const url = window.location.href;
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];
    setDropskipToolId(lastPart);
    if (lastPart === "create") {
      return;
    } else {
      fetchDropskipToolById(lastPart);
    }
  }, [dropskipToolId]);

  useEffect(() => {
    if (selectedDropskipTool) {
      // Ensure existing data has primary property in merchandise items
      const updatedMerchandise = selectedDropskipTool.merchandise.map(
        (item) => ({
          ...item,
          primary: item.primary || false,
        }),
      );
      setFormData({
        ...selectedDropskipTool,
        merchandise: updatedMerchandise,
      });
    } else {
      resetForm();
    }
  }, [selectedDropskipTool]);

  const resetForm = () => {
    setFormData({
      category: "",
      avg_order_weight: "",
      avg_units_per_order: "",
      avg_order_value: "",
      cogs: "",
      unit_cogs: "",
      b2b_fulfillment_cost: "",
      ds_fulfillment_cost: "",
      merchandise: [
        {
          name: "",
          hs_code: "",
          duty_non_china: "",
          duty_rate_china: "",
          primary: false,
        },
      ],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMerchandiseChange = (index, e) => {
    const { name, value } = e.target;
    const updatedMerchandise = [...formData.merchandise];
    updatedMerchandise[index][name] = value;
    setFormData({ ...formData, merchandise: updatedMerchandise });
  };

  const handlePrimaryChange = (index) => {
    const updatedMerchandise = formData.merchandise.map((item, i) => ({
      ...item,
      primary: i === index,
    }));
    setFormData({ ...formData, merchandise: updatedMerchandise });
  };

  const addMerchandiseRow = () => {
    setFormData({
      ...formData,
      merchandise: [
        ...formData.merchandise,
        {
          name: "",
          hs_code: "",
          duty_non_china: "",
          duty_rate_china: "",
          primary: false,
        },
      ],
    });
  };

  const removeMerchandiseRow = (index) => {
    const updatedMerchandise = formData.merchandise.filter(
      (_, i) => i !== index,
    );
    setFormData({ ...formData, merchandise: updatedMerchandise });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate that at least one merchandise item is marked as primary
    const hasPrimaryItem = formData.merchandise.some(
      (item) => item.primary === true,
    );

    if (!hasPrimaryItem) {
      toast.error("Please select at least one merchandise item as primary.");
      return;
    }

    // Convert relevant fields to numbers
    const convertedFormData = {
      ...formData,
      avg_order_weight: Number(formData.avg_order_weight),
      avg_units_per_order: Number(formData.avg_units_per_order),
      avg_order_value: Number(formData.avg_order_value),
      cogs: Number(formData.cogs),
      unit_cogs: Number(formData.unit_cogs),
      b2b_fulfillment_cost: Number(formData.b2b_fulfillment_cost),
      ds_fulfillment_cost: Number(formData.ds_fulfillment_cost),
      merchandise: formData.merchandise.map((item) => ({
        ...item,
        duty_non_china: Number(item.duty_non_china),
        duty_rate_china: Number(item.duty_rate_china),
      })),
    };

    try {
      if (selectedDropskipTool) {
        await marketDataService.edit(
          selectedDropskipTool._id,
          convertedFormData,
        );
        toast.success("Data updated successfully!");
      } else {
        await marketDataService.create(convertedFormData);
        toast.success("Data created successfully!");
      }

      resetForm();
      localStorage.setItem("path", `/market-data`);
      navigate("/market-data");
    } catch (e) {
      toast.error("Failed to save data.");
      console.error(e);
    }
  };

  // Required field label with star
  const RequiredLabel = ({ text }) => (
    <Box
      component="span"
      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
    >
      {text}{" "}
      <Typography color="error" component="span" sx={{ ml: 0.5 }}>
        *
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ height: "100vh" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ mt: 1 }} variant="h6">
          {selectedDropskipTool ? "Edit" : "Create"} Market Data
        </Typography>
      </div>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: "100vh",
          maxHeight: "calc(100vh - 250px)",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={3} sx={{ flex: 1, height: "100%" }}>
          {/* Left Column - Form Fields */}
          <Grid item xs={12} md={5} sx={{ height: "100%", overflowY: "auto" }}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                General Information
              </Typography>
              <Box
                sx={{
                  // maxHeight: { xs: "50vh", md: "60vh" },
                  overflowY: "auto",
                  flex: 1,
                  pr: 1,
                }}
              >
                <Box>
                  <RequiredLabel text="Category" />
                  <TextField
                    size="small"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="Avg Order Weight" />
                  <TextField
                    size="small"
                    name="avg_order_weight"
                    value={formData.avg_order_weight}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="Avg Units per Order" />
                  <TextField
                    size="small"
                    name="avg_units_per_order"
                    value={formData.avg_units_per_order}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="Avg Order Value" />
                  <TextField
                    size="small"
                    name="avg_order_value"
                    value={formData.avg_order_value}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="COGS (%)" />
                  <TextField
                    size="small"
                    name="cogs"
                    value={formData.cogs}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{ endAdornment: "%" }}
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="Unit COGS" />
                  <TextField
                    size="small"
                    name="unit_cogs"
                    value={formData.unit_cogs}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="B2B Fulfillment Cost" />
                  <TextField
                    size="small"
                    name="b2b_fulfillment_cost"
                    value={formData.b2b_fulfillment_cost}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <RequiredLabel text="DS Fulfillment Cost" />
                  <TextField
                    size="small"
                    name="ds_fulfillment_cost"
                    value={formData.ds_fulfillment_cost}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Merchandise */}
          <Grid item xs={12} md={7} sx={{ height: "100%", overflowY: "auto" }}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Merchandise Items ({formData.merchandise.length})
                </Typography>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addMerchandiseRow}
                  variant="contained"
                  size="small"
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  Add Item
                </Button>
              </Box>

              <Box
                sx={{
                  // maxHeight: { xs: "50vh", md: "58vh" },
                  overflowY: "auto",
                  flex: 1,
                  pr: 1,
                }}
              >
                {formData.merchandise.map((item, index) => (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      position: "relative",
                      "&:hover .delete-btn": { opacity: 1 },
                    }}
                    key={index}
                  >
                    <IconButton
                      className="delete-btn"
                      onClick={() => removeMerchandiseRow(index)}
                      color="error"
                      disabled={formData.merchandise.length <= 1}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        opacity: formData.merchandise.length <= 1 ? 0.3 : 0.7,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>

                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      Item #{index + 1}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <RequiredLabel text="Merchandise Name" />
                        <TextField
                          size="small"
                          name="name"
                          value={item.name}
                          onChange={(e) => handleMerchandiseChange(index, e)}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <RequiredLabel text="HS Code" />
                        <TextField
                          size="small"
                          name="hs_code"
                          value={item.hs_code}
                          onChange={(e) => handleMerchandiseChange(index, e)}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <RequiredLabel text="Duty Rate - Non China" />
                        <TextField
                          size="small"
                          name="duty_non_china"
                          value={item.duty_non_china}
                          onChange={(e) => handleMerchandiseChange(index, e)}
                          fullWidth
                          InputProps={{ endAdornment: "%" }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <RequiredLabel text="Duty Rate China" />
                        <TextField
                          size="small"
                          name="duty_rate_china"
                          value={item.duty_rate_china}
                          onChange={(e) => handleMerchandiseChange(index, e)}
                          fullWidth
                          InputProps={{ endAdornment: "%" }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={item.primary}
                              onChange={() => handlePrimaryChange(index)}
                            />
                          }
                          label="Set as primary merchandise"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "fixed",
          bottom: 5,
          left: -10,
          right: 0,
          padding: "5px",
          backgroundColor: "var(--bg-primary)",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Button
          onClick={() => {
            localStorage.setItem("path", `/market-data`);
            navigate("/market-data");
          }}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {selectedDropskipTool ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default ManageMarketData;
